/**
 * Mobile deep link — dash data layer (feature #176 / FS-176).
 *
 * Thin typed wrappers over `apiClient` for /api/v1/mobile-deep-link.
 * Pure, no React imports so the module is directly unit-testable.
 *
 * Domain:
 *   Each row binds a campaign + native URL scheme to a path template with
 *   `{macro}` placeholders. The resolver inspects the user's UA, picks
 *   ios / android / web, and returns the deep-link URL (native scheme or
 *   https fallback).
 *
 * Endpoints wrapped:
 *   GET    /mobile-deep-link                  (list; ?campaign_id=)
 *   POST   /mobile-deep-link                  (create)
 *   PATCH  /mobile-deep-link/:id              (partial update)
 *   DELETE /mobile-deep-link/:id              (soft delete)
 *   POST   /mobile-deep-link/resolve          (run the resolver)
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export const PLATFORMS = ["ios", "android", "web"] as const;
export type Platform = (typeof PLATFORMS)[number];

export interface MobileDeepLink {
	id: string;
	campaign_id: string;
	scheme: string;
	host: string;
	path_template: string;
	fallback_url: string;
	active: boolean;
	created_at: number;
	updated_at: number;
}

export interface DeepLinkListResponse {
	count: number;
	deep_links: MobileDeepLink[];
}

export interface ResolveResponse {
	deep_link_url: string | null;
	fallback_url: string | null;
	platform: Platform;
}

export interface CreateDeepLinkInput {
	campaignId: string;
	scheme: string;
	host?: string;
	pathTemplate?: string;
	fallbackUrl: string;
	active?: boolean;
}

export interface UpdateDeepLinkInput {
	scheme?: string;
	host?: string;
	pathTemplate?: string;
	fallbackUrl?: string;
	active?: boolean;
}

export interface ResolveInput {
	campaignId: string;
	ua?: string;
	context?: Record<string, string | number | boolean>;
}

// ---------------------------------------------------------------------------
// Pure form helpers (exported for unit tests)
// ---------------------------------------------------------------------------

const CAMPAIGN_ID_PATTERN = /^[A-Za-z0-9_.\-]{1,64}$/;
const SCHEME_PATTERN = /^[a-z][a-z0-9+.\-]{0,39}$/;
const SCHEME_BLOCKLIST = new Set(["javascript", "data", "file"]);
const HOST_MAX = 253;
const HOST_PATTERN = /^[A-Za-z0-9._-]+(?::[0-9]{1,5})?$/;
const PATH_MAX = 512;
const FALLBACK_MAX = 2048;

export function isValidCampaignId(raw: unknown): raw is string {
	return typeof raw === "string" && CAMPAIGN_ID_PATTERN.test(raw);
}

export function normalizeScheme(raw: unknown): string | null {
	if (typeof raw !== "string") return null;
	let s = raw.trim().toLowerCase();
	if (s.endsWith("://")) s = s.slice(0, -3);
	if (!SCHEME_PATTERN.test(s)) return null;
	if (SCHEME_BLOCKLIST.has(s)) return null;
	return s;
}

export function normalizeHost(raw: unknown): string | null {
	if (raw === undefined || raw === null || raw === "") return "";
	if (typeof raw !== "string") return null;
	const h = raw.trim().toLowerCase();
	if (h === "") return "";
	if (h.length > HOST_MAX) return null;
	if (!HOST_PATTERN.test(h)) return null;
	return h;
}

export function normalizePathTemplate(raw: unknown): string | null {
	if (raw === undefined || raw === null || raw === "") return "";
	if (typeof raw !== "string") return null;
	const t = raw.trim();
	if (t.length > PATH_MAX) return null;
	if (/[\s<>"'`\u0000-\u001F]/.test(t)) return null;
	let clean = t;
	while (clean.startsWith("/")) clean = clean.slice(1);
	return clean;
}

export function normalizeFallbackUrl(raw: unknown): string | null {
	if (typeof raw !== "string") return null;
	const s = raw.trim();
	if (!s || s.length > FALLBACK_MAX) return null;
	let parsed: URL;
	try {
		parsed = new URL(s);
	} catch {
		return null;
	}
	if (parsed.protocol !== "https:") return null;
	return parsed.toString();
}

/**
 * Substitute `{macro}` placeholders against a context map (scalar values
 * only; URI-component encoded). Mirrors the server-side composer exactly
 * so the UI can preview without a round-trip.
 */
export function substituteMacros(
	template: string,
	context: Record<string, string | number | boolean> | null | undefined,
): string {
	if (!template) return "";
	const ctx = context ?? {};
	return template.replace(
		/\{([A-Za-z][A-Za-z0-9_]*)\}/g,
		(whole, name: string) => {
			const v = ctx[name];
			if (v === undefined || v === null) return whole;
			return encodeURIComponent(String(v));
		},
	);
}

export function buildDeepLinkPreview(
	row: Pick<MobileDeepLink, "scheme" | "host" | "path_template">,
	context?: Record<string, string | number | boolean>,
): string {
	const path = substituteMacros(row.path_template, context);
	const base = `${row.scheme}://${row.host}`;
	if (!path) return base;
	return `${base}/${path}`;
}

export function platformLabel(p: Platform): string {
	switch (p) {
		case "ios":
			return "iOS";
		case "android":
			return "Android";
		case "web":
			return "Web";
	}
}

export function extractMacros(template: string): string[] {
	if (!template) return [];
	const out = new Set<string>();
	const re = /\{([A-Za-z][A-Za-z0-9_]*)\}/g;
	let m: RegExpExecArray | null;
	while ((m = re.exec(template)) !== null) {
		out.add(m[1]);
	}
	return Array.from(out);
}

// ---------------------------------------------------------------------------
// Form parse
// ---------------------------------------------------------------------------

export interface DeepLinkFormInput {
	campaignId?: unknown;
	scheme?: unknown;
	host?: unknown;
	pathTemplate?: unknown;
	fallbackUrl?: unknown;
	active?: unknown;
}

export type DeepLinkFormResult =
	| { success: true; data: Required<CreateDeepLinkInput> }
	| { success: false; errors: Record<string, string> };

export const deepLinkFormSchema = {
	safeParse(input: DeepLinkFormInput): DeepLinkFormResult {
		const errors: Record<string, string> = {};

		const campaignId =
			typeof input.campaignId === "string" ? input.campaignId.trim() : "";
		if (!isValidCampaignId(campaignId)) {
			errors.campaignId =
				"Campaign ID must be 1-64 chars, letters/digits/._-";
		}

		const scheme = normalizeScheme(input.scheme);
		if (!scheme) {
			errors.scheme =
				"Scheme must start with a letter, contain only a-z 0-9 + . -, and not be javascript/data/file";
		}

		const host = normalizeHost(input.host);
		if (host === null) errors.host = "Host contains invalid characters";

		const pathTemplate = normalizePathTemplate(input.pathTemplate);
		if (pathTemplate === null) {
			errors.pathTemplate = "Path template contains invalid characters";
		}

		const fallbackUrl = normalizeFallbackUrl(input.fallbackUrl);
		if (!fallbackUrl) {
			errors.fallbackUrl = "Fallback must be an absolute https:// URL";
		}

		const active =
			input.active === undefined ? true : Boolean(input.active);

		if (Object.keys(errors).length > 0) {
			return { success: false, errors };
		}
		return {
			success: true,
			data: {
				campaignId: campaignId as string,
				scheme: scheme as string,
				host: host ?? "",
				pathTemplate: pathTemplate ?? "",
				fallbackUrl: fallbackUrl as string,
				active,
			},
		};
	},
};

// ---------------------------------------------------------------------------
// Canonical preview UAs used by the dash simulator.
// ---------------------------------------------------------------------------

export const SAMPLE_UAS: { label: string; ua: string; platform: Platform }[] = [
	{
		label: "iOS Safari (iPhone)",
		ua: "Mozilla/5.0 (iPhone; CPU iPhone OS 17_2 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.0 Mobile/15E148 Safari/604.1",
		platform: "ios",
	},
	{
		label: "Android Chrome (Pixel 8)",
		ua: "Mozilla/5.0 (Linux; Android 14; Pixel 8) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/121.0.0.0 Mobile Safari/537.36",
		platform: "android",
	},
	{
		label: "Desktop Firefox",
		ua: "Mozilla/5.0 (X11; Linux x86_64; rv:121.0) Gecko/20100101 Firefox/121.0",
		platform: "web",
	},
];

// ---------------------------------------------------------------------------
// HTTP bindings
// ---------------------------------------------------------------------------

export async function fetchDeepLinks(params?: {
	campaignId?: string;
}): Promise<MobileDeepLink[]> {
	const query: Record<string, string> = {};
	if (params?.campaignId && isValidCampaignId(params.campaignId)) {
		query.campaign_id = params.campaignId;
	}
	try {
		const res = await apiClient.get<DeepLinkListResponse>(
			"/mobile-deep-link",
			{ query: Object.keys(query).length ? query : undefined },
		);
		return res?.deep_links ?? [];
	} catch (err) {
		if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
			return [];
		}
		throw err;
	}
}

export async function createDeepLink(
	input: CreateDeepLinkInput,
): Promise<MobileDeepLink> {
	const body: Record<string, unknown> = {
		campaign_id: input.campaignId,
		scheme: input.scheme,
		host: input.host ?? "",
		path_template: input.pathTemplate ?? "",
		fallback_url: input.fallbackUrl,
	};
	if (input.active !== undefined) body.active = input.active;
	const res = await apiClient.post<{ deep_link: MobileDeepLink }>(
		"/mobile-deep-link",
		body,
	);
	return res.deep_link;
}

export async function updateDeepLink(
	id: string,
	input: UpdateDeepLinkInput,
): Promise<MobileDeepLink> {
	const body: Record<string, unknown> = {};
	if (input.scheme !== undefined) body.scheme = input.scheme;
	if (input.host !== undefined) body.host = input.host;
	if (input.pathTemplate !== undefined) body.path_template = input.pathTemplate;
	if (input.fallbackUrl !== undefined) body.fallback_url = input.fallbackUrl;
	if (input.active !== undefined) body.active = input.active;
	const res = await apiClient.patch<{ deep_link: MobileDeepLink }>(
		`/mobile-deep-link/${encodeURIComponent(id)}`,
		body,
	);
	return res.deep_link;
}

export async function deleteDeepLink(
	id: string,
): Promise<{ deleted: boolean; id: string }> {
	return apiClient.delete<{ deleted: boolean; id: string }>(
		`/mobile-deep-link/${encodeURIComponent(id)}`,
	);
}

export async function resolveDeepLink(
	input: ResolveInput,
): Promise<ResolveResponse> {
	const body: Record<string, unknown> = { campaign_id: input.campaignId };
	if (input.ua !== undefined) body.ua = input.ua;
	if (input.context !== undefined) body.context = input.context;
	return apiClient.post<ResolveResponse>(
		"/mobile-deep-link/resolve",
		body,
	);
}

export const mobileDeepLinkPaths = {
	base: "/mobile-deep-link",
	one: (id: string) => `/mobile-deep-link/${encodeURIComponent(id)}`,
	resolve: "/mobile-deep-link/resolve",
} as const;
